import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Gift, Sparkles, Instagram, Phone, ChevronLeft, ChevronRight, History, ExternalLink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { useSaleById } from '@/hooks/useSales';
import { SaleDetails } from '@/components/sales/SaleDetails';
import { useLoyaltyCustomerHistory } from './hooks';

const PAGE_SIZE = 20;

interface LoyaltyCustomerHistoryDrawerProps {
  customerId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const reasonLabel = (reason: string): string => {
  switch (reason) {
    case 'sale_paid':
      return 'Venda paga';
    case 'sale_cancelled':
      return 'Venda cancelada';
    case 'sale_repaid':
      return 'Venda reaberta';
    default:
      return reason;
  }
};

export const LoyaltyCustomerHistoryDrawer = ({
  customerId,
  open,
  onOpenChange,
}: LoyaltyCustomerHistoryDrawerProps) => {
  const [page, setPage] = useState(0);
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);
  const { data: selectedSale = null } = useSaleById(selectedSaleId);

  useEffect(() => {
    if (open) setPage(0);
  }, [open, customerId]);

  const { data, isLoading, isFetching } = useLoyaltyCustomerHistory(
    open ? customerId : null,
    page,
    PAGE_SIZE,
  );

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const customer = data?.customer;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-base">
            {customer?.name ?? 'Carregando...'}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Contact */}
          {(customer?.whatsapp || customer?.instagram) && (
            <div className="flex flex-wrap gap-2">
              {customer.instagram && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-pink-600 bg-pink-50 rounded-full px-2 py-0.5">
                  <Instagram className="h-3 w-3" />
                  @{customer.instagram.replace('@', '')}
                </span>
              )}
              {customer.whatsapp && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                  <Phone className="h-3 w-3" />
                  {customer.whatsapp}
                </span>
              )}
            </div>
          )}

          {/* Balance cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-[#C2DBF6]/40 bg-[#EAEFFE]/30 p-4">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <Gift className="h-3 w-3" />
                Saldo atual
              </div>
              <div className="text-xl font-semibold text-[#574BFB] mt-1">
                {isLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <>
                    {(data?.balance ?? 0).toLocaleString('pt-BR')}{' '}
                    <span className="text-xs font-normal text-muted-foreground">pts</span>
                  </>
                )}
              </div>
            </div>
            <div className="rounded-xl border border-border/60 p-4">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                Total acumulado
              </div>
              <div className="text-xl font-semibold text-foreground mt-1">
                {isLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  (data?.lifetime_earned ?? 0).toLocaleString('pt-BR')
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* History */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <History className="h-3.5 w-3.5" />
                Histórico de movimentações
              </div>
              {data && data.total > 0 && (
                <span className="text-[11px] text-muted-foreground">
                  {data.total.toLocaleString('pt-BR')} no total
                </span>
              )}
            </div>

            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </div>
            ) : !data?.rows || data.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                Nenhuma movimentação encontrada.
              </p>
            ) : (
              <div className="space-y-1">
                {data.rows.map((row) => {
                  const hasSale = !!row.sale_id;
                  const content = (
                    <>
                      <div className="flex flex-col min-w-0 items-start">
                        <span className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">
                          {reasonLabel(row.reason)}
                          {hasSale && (
                            <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          )}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {format(new Date(row.created_at), "dd 'de' MMM, HH:mm", { locale: ptBR })}
                        </span>
                      </div>
                      <span
                        className={`text-sm font-semibold ml-3 shrink-0 ${
                          row.points >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}
                      >
                        {row.points >= 0 ? '+' : ''}
                        {row.points.toLocaleString('pt-BR')}
                      </span>
                    </>
                  );

                  if (hasSale) {
                    return (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => setSelectedSaleId(row.sale_id)}
                        className="group flex w-full items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors text-left cursor-pointer"
                      >
                        {content}
                      </button>
                    );
                  }

                  return (
                    <div
                      key={row.id}
                      className="flex items-center justify-between py-2 px-3 rounded-lg"
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0 || isFetching}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                  Anterior
                </Button>
                <span className="text-xs text-muted-foreground">
                  Página {page + 1} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1 || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                  <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </SheetContent>

      <SaleDetails
        open={!!selectedSaleId}
        onOpenChange={(isOpen) => !isOpen && setSelectedSaleId(null)}
        sale={selectedSale}
      />
    </Sheet>
  );
};
